import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { clientes as apiClientes } from "../lib/api";
import { formatCurrency, tiempoRelativo, formatDateTime } from "../lib/formato";
import PaginationControls from "../componentes/PaginationControls";
import { SearchIcon } from "../componentes/Iconos";

const POR_PAGINA = 20;

const FILTROS = [
  { valor: "todos", nombre: "Todos" },
  { valor: "deudores", nombre: "Deben" },
  { valor: "al_dia", nombre: "Al día" },
];

const ORDENES = [
  { valor: "deuda_desc", nombre: "Los que más deben" },
  { valor: "deuda_asc", nombre: "Los que menos deben" },
  { valor: "reciente", nombre: "Movimiento más reciente" },
  { valor: "antiguo", nombre: "Más tiempo sin aparecer" },
  { valor: "nombre", nombre: "Nombre (A-Z)" },
];

// Pantalla para revisar la cartera: quién debe más, quién menos y quién
// lleva rato sin aparecer. El filtro, el orden y la página van en la URL.
const SaldosPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pagina = Number(searchParams.get("page")) || 1;
  const busqueda = searchParams.get("q") || "";
  const filtro = searchParams.get("filtro") || "deudores";
  const orden = searchParams.get("orden") || "deuda_desc";

  const [textoBuscado, setTextoBuscado] = useState(busqueda);
  const [datos, setDatos] = useState({ items: [], totalPages: 0 });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const cambiarParams = useCallback(
    (cambios) => {
      const params = { filtro, orden };
      if (busqueda) params.q = busqueda;
      Object.entries(cambios).forEach(([clave, valor]) => {
        if (valor === null || valor === "" || valor === undefined) delete params[clave];
        else params[clave] = valor;
      });
      setSearchParams(params);
    },
    [filtro, orden, busqueda, setSearchParams]
  );

  useEffect(() => {
    setTextoBuscado(busqueda);
  }, [busqueda]);

  useEffect(() => {
    if (textoBuscado === busqueda) return;
    const temporizador = setTimeout(() => {
      cambiarParams({ q: textoBuscado, page: null });
    }, 300);
    return () => clearTimeout(temporizador);
  }, [textoBuscado, busqueda, cambiarParams]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setDatos(
        await apiClientes.listar({ pagina, limite: POR_PAGINA, busqueda, orden, filtro })
      );
    } catch (err) {
      setError(err.message);
      setDatos({ items: [], totalPages: 0 });
    } finally {
      setCargando(false);
    }
  }, [pagina, busqueda, orden, filtro]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  return (
    <div className="saldos-view">
      <div className="toolbar toolbar-saldos">
        <div className="search-bar">
          <SearchIcon />
          <input
            type="text"
            placeholder="Buscar por nombre o teléfono..."
            value={textoBuscado}
            onChange={(e) => setTextoBuscado(e.target.value)}
          />
        </div>
      </div>

      <div className="filtros-saldos">
        <div className="grupo-filtro" role="group" aria-label="Filtrar clientes">
          {FILTROS.map((opcion) => (
            <button
              key={opcion.valor}
              type="button"
              className="chip-filtro"
              aria-pressed={filtro === opcion.valor}
              onClick={() => cambiarParams({ filtro: opcion.valor, page: null })}
            >
              {opcion.nombre}
            </button>
          ))}
        </div>

        <label className="selector-orden">
          <span>Ordenar por</span>
          <select
            value={orden}
            onChange={(e) => cambiarParams({ orden: e.target.value, page: null })}
          >
            {ORDENES.map((opcion) => (
              <option key={opcion.valor} value={opcion.valor}>
                {opcion.nombre}
              </option>
            ))}
          </select>
        </label>
      </div>

      {cargando ? (
        <p>Cargando...</p>
      ) : error ? (
        <p className="error-message">{error}</p>
      ) : datos.items.length === 0 ? (
        <p className="lista-sin-resultados">No hay clientes que cumplan con esos criterios.</p>
      ) : (
        <>
          <div className="tabla-saldos-contenedor">
            <table className="tabla-saldos">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th className="col-telefono">Teléfono</th>
                  <th className="col-movimiento">Último movimiento</th>
                  <th className="num col-saldo">Saldo pendiente</th>
                </tr>
              </thead>
              <tbody>
                {datos.items.map((cliente) => {
                  const saldo = Number(cliente.saldo_pendiente) || 0;
                  return (
                    <tr key={cliente.id}>
                      <td>
                        <Link to={`/clientes/${cliente.id}`} className="enlace-cliente">
                          {cliente.nombre_completo}
                        </Link>
                      </td>
                      <td className="col-telefono">{cliente.telefono}</td>
                      <td
                        className="col-movimiento"
                        title={
                          cliente.ultimo_movimiento
                            ? formatDateTime(cliente.ultimo_movimiento)
                            : undefined
                        }
                      >
                        {tiempoRelativo(cliente.ultimo_movimiento)}
                      </td>
                      <td className="num col-saldo">
                        <span className={saldo > 0 ? "saldo-debe" : "saldo-al-dia"}>
                          {saldo > 0 ? formatCurrency(saldo) : "Al día"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <PaginationControls
            currentPage={pagina}
            totalPages={datos.totalPages}
            onPageChange={(nueva) => {
              cambiarParams({ page: nueva > 1 ? nueva : null });
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </>
      )}
    </div>
  );
};

export default SaldosPage;
